import { useState, useEffect } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import api from "../utils/api";

const CopyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const ProductCardModal = ({ product, options, canEdit, onClose, onSaved, onAddQr, onRefresh }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [urls, setUrls] = useState(product.urls || []);
  const [deletedUrls, setDeletedUrls] = useState(product.deletedUrls || []);
  const [showDeletedQr, setShowDeletedQr] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [auditLog, setAuditLog] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  // QR code inline edit
  const [editingQr, setEditingQr] = useState(null);
  const [qrForm, setQrForm] = useState({});

  // QR code selection for ZIP download
  const [selectedQrs, setSelectedQrs] = useState(new Set());
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setForm({
      productName: product.productName || "",
      upc: product.upc || "",
      itemId: product.itemId || "",
      retailer: product.retailer || "",
      customerItemNumber: product.customerItemNumber || "",
      notes: product.notes || "",
    });
    setUrls(product.urls || []);
    setDeletedUrls(product.deletedUrls || []);
  }, [product]);

  const refreshProduct = async () => {
    try {
      const res = await api.get(`/api/products/${product._id}`);
      setUrls(res.data.urls);
      setDeletedUrls(res.data.deletedUrls || []);
      onRefresh();
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    setError("");
    setSaving(true);
    try {
      await api.put(`/api/products/${product._id}`, form);
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${product.productName}" and all its QR codes?`)) return;
    try {
      await api.delete(`/api/products/${product._id}`);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || "Delete failed");
    }
  };

  const handleDeleteQr = async (urlId) => {
    if (!window.confirm("Delete this QR code?")) return;
    try {
      await api.get(`/d/${urlId}`);
      await refreshProduct();
    } catch (err) {
      console.error(err);
    }
  };

  const restoreQr = async (urlId) => {
    try {
      await api.put(`/api/urls/${urlId}/restore`);
      await refreshProduct();
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveQr = async () => {
    try {
      await api.put(`/api/urls/${editingQr}`, qrForm);
      setEditingQr(null);
      await refreshProduct();
    } catch (err) {
      setError(err.response?.data?.error || "Save failed");
    }
  };

  const toggleDeprecated = async (url) => {
    try {
      await api.put(`/api/urls/${url.urlId}`, { isDeprecated: !url.isDeprecated });
      await refreshProduct();
    } catch (err) {
      console.error(err);
    }
  };

  const loadAudit = async () => {
    try {
      const res = await api.get(`/api/products/${product._id}/audit`);
      setAuditLog(res.data);
      setShowAudit(true);
    } catch (err) {
      console.error(err);
    }
  };

  const copyToClipboard = (url, urlId) => {
    navigator.clipboard.writeText(url);
    setCopiedId(urlId);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const toggleQrSelection = (urlId) => {
    setSelectedQrs((prev) => {
      const next = new Set(prev);
      if (next.has(urlId)) next.delete(urlId);
      else next.add(urlId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedQrs.size === urls.length) {
      setSelectedQrs(new Set());
    } else {
      setSelectedQrs(new Set(urls.map((u) => u.urlId)));
    }
  };

  const downloadSelectedQrs = async () => {
    if (selectedQrs.size === 0) return;
    setDownloading(true);

    try {
      const zip = new JSZip();
      const prodName = (product.productName || "Product").replace(/\s+/g, "_");
      const itemId = product.itemId || "NOID";
      const upc = product.upc || "NOUPC";
      const dateStr = new Date().toISOString().slice(0, 10);

      for (const url of urls) {
        if (!selectedQrs.has(url.urlId)) continue;
        if (url.isDeprecated) continue;
        const response = await fetch(`/qr/${url.urlId}.svg`);
        if (response.ok) {
          const svgBlob = await response.blob();
          const side = (url.side || "other").charAt(0).toUpperCase() + (url.side || "other").slice(1);
          let fileName;
          if (url.qrFormat === "gs1" && url.gtin) {
            fileName = `${itemId}-GS1_${url.gtin}-${prodName}-${side}.svg`;
          } else {
            fileName = `${itemId}-${upc}-${prodName}-${url.urlId}-${side}.svg`;
          }
          zip.file(fileName, svgBlob);
        }
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const zipName = `${itemId}-${upc}-${prodName}-${dateStr}.zip`;
      saveAs(zipBlob, zipName);
      setSelectedQrs(new Set());
    } catch (err) {
      console.error("ZIP download error:", err);
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-CA") + " " + d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
  };

  const fieldLabel = (label) => (
    <div className="text-muted" style={{ fontSize: "11px", fontWeight: 500 }}>{label}</div>
  );

  return (
    <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content" style={{ fontSize: "14px" }}>
          {/* Header bar */}
          <div className="modal-header py-2" style={{ backgroundColor: "#f0f0f0", borderBottom: "2px solid #0078d4" }}>
            <div>
              <h6 className="mb-0">{product.productName}</h6>
              <small className="text-muted">Product Card</small>
            </div>
            <div className="d-flex align-items-center gap-1">
              {canEdit && !editing && (
                <>
                  <button
                    className="btn btn-sm btn-link text-muted p-1"
                    onClick={handleDelete}
                    title="Delete product"
                    style={{ lineHeight: 1 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                  </button>
                  <div className="flex-grow-1" />
                  <button className="btn btn-sm btn-outline-primary" onClick={() => setEditing(true)}>Edit</button>
                  <button className="btn btn-sm btn-outline-success" onClick={() => onAddQr(product)}>+ QR Code</button>
                </>
              )}
              {editing && (
                <>
                  <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                  </button>
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditing(false)}>Cancel</button>
                </>
              )}
              <button className="btn-close ms-2" onClick={onClose} />
            </div>
          </div>

          <div className="modal-body p-0">
            {error && <div className="alert alert-danger m-3 py-2 mb-0">{error}</div>}

            {showAudit ? (
              <div className="p-3">
                <button className="btn btn-sm btn-outline-secondary mb-3" onClick={() => setShowAudit(false)} style={{ fontSize: "11px" }}>
                  Back to Product
                </button>
                <h6 style={{ fontSize: "14px" }}>Change History</h6>
                {auditLog && auditLog.length > 0 ? (
                  <table className="table table-sm" style={{ fontSize: "13px" }}>
                    <thead><tr><th>Date</th><th>Field</th><th>Old Value</th><th>New Value</th><th>Changed By</th></tr></thead>
                    <tbody>
                      {auditLog.map((log, i) => (
                        <tr key={i}>
                          <td className="text-muted">{formatDate(log.changedAt)}</td>
                          <td>{log.field}</td>
                          <td className="text-muted">{log.oldValue || "—"}</td>
                          <td>{log.newValue}</td>
                          <td className="text-muted">{log.changedBy}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-muted small">No change history.</p>
                )}
              </div>
            ) : (
              <>
                {/* Top section — Product details (BC-style header fields) */}
                <div className="p-3" style={{ backgroundColor: "#fafafa", borderBottom: "1px solid #dee2e6" }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", color: "#666" }}>General</span>
                    <button className="btn btn-link btn-sm p-0 text-decoration-none" style={{ fontSize: "11px" }} onClick={loadAudit}>
                      History
                    </button>
                  </div>
                  <div className="row">
                    <div className="col-md-4 col-sm-6 mb-2">
                      {fieldLabel("Product Name")}
                      {editing ? (
                        <input className="form-control form-control-sm" value={form.productName} onChange={(e) => setForm({ ...form, productName: e.target.value })} />
                      ) : (
                        <div style={{ fontSize: "14px" }}>{product.productName || "—"}</div>
                      )}
                    </div>
                    <div className="col-md-4 col-sm-6 mb-2">
                      {fieldLabel("UPC")}
                      {editing ? (
                        <input className="form-control form-control-sm" value={form.upc} onChange={(e) => setForm({ ...form, upc: e.target.value })} />
                      ) : (
                        <div style={{ fontSize: "14px" }}>{product.upc || "—"}</div>
                      )}
                    </div>
                    <div className="col-md-4 col-sm-6 mb-2">
                      {fieldLabel("Item ID")}
                      {editing ? (
                        <input className="form-control form-control-sm" value={form.itemId} onChange={(e) => setForm({ ...form, itemId: e.target.value })} />
                      ) : (
                        <div style={{ fontSize: "14px" }}>{product.itemId || "—"}</div>
                      )}
                    </div>
                    <div className="col-md-4 col-sm-6 mb-2">
                      {fieldLabel("Retailer")}
                      {editing ? (
                        <select className="form-select form-select-sm" value={form.retailer} onChange={(e) => setForm({ ...form, retailer: e.target.value })}>
                          <option value="">Select...</option>
                          {options?.retailers?.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      ) : (
                        <div style={{ fontSize: "14px" }}>{product.retailer || "—"}</div>
                      )}
                    </div>
                    <div className="col-md-4 col-sm-6 mb-2">
                      {fieldLabel("Customer Item #")}
                      {editing ? (
                        <input className="form-control form-control-sm" value={form.customerItemNumber} onChange={(e) => setForm({ ...form, customerItemNumber: e.target.value })} />
                      ) : (
                        <div style={{ fontSize: "14px" }}>{product.customerItemNumber || "—"}</div>
                      )}
                    </div>
                    <div className="col-md-4 col-sm-6 mb-2">
                      {fieldLabel("Notes")}
                      {editing ? (
                        <input className="form-control form-control-sm" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                      ) : (
                        <div style={{ fontSize: "14px" }}>{product.notes || "—"}</div>
                      )}
                    </div>
                    <div className="col-md-4 col-sm-6 mb-2">
                      {fieldLabel("Stats")}
                      <div style={{ fontSize: "14px" }}>
                        {urls.length} QR code{urls.length !== 1 ? "s" : ""} &middot; {product.totalClicks?.toLocaleString() || 0} clicks &middot; Created {formatDate(product.createdAt)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom section — QR Codes grid */}
                <div className="p-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", color: "#666" }}>QR Codes</span>
                  </div>

                  {urls.length > 0 ? (
                    <table className="table table-sm table-hover mb-0" style={{ fontSize: "13px" }}>
                      <thead>
                        <tr style={{ backgroundColor: "#f8f9fa" }}>
                          <th className="px-1" style={{ width: "30px" }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={urls.length > 0 && selectedQrs.size === urls.length}
                              onChange={toggleSelectAll}
                              style={{ cursor: "pointer" }}
                            />
                          </th>
                          <th className="px-2">Side</th>
                          <th className="px-2">Short URL</th>
                          <th className="px-2">QR</th>
                          <th className="px-2">Destination</th>
                          <th className="px-2">Notes</th>
                          <th className="px-2">Created</th>
                          <th className="px-2 text-end">Clicks</th>
                          {canEdit && <th className="px-2"></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {urls.map((url) => (
                          <tr key={url.urlId} style={url.isDeprecated ? { opacity: 0.4 } : {}}>
                            <td className="px-1 align-middle">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedQrs.has(url.urlId)}
                                onChange={() => toggleQrSelection(url.urlId)}
                                onClick={(e) => e.stopPropagation()}
                                style={{ cursor: "pointer" }}
                              />
                            </td>
                            <td className="px-2 align-middle">
                              <span className={`badge ${url.side === "front" ? "bg-primary" : url.side === "back" ? "bg-secondary" : "bg-warning text-dark"}`} style={{ fontSize: "10px" }}>
                                {url.side || "—"}
                              </span>
                              {url.qrFormat === "legacy" && (
                                <span className="badge bg-light text-muted border ms-1" style={{ fontSize: "9px" }}>legacy</span>
                              )}
                              {url.qrFormat === "gs1" && (
                                <span className="badge bg-success ms-1" style={{ fontSize: "9px" }}>GS1</span>
                              )}
                            </td>
                            <td className="px-2 align-middle text-nowrap">
                              {editingQr === url.urlId ? (
                                <span className="text-muted">{url.qrFormat === "gs1" && url.gtin ? url.gtin : url.urlId}</span>
                              ) : (
                                <span className="d-flex align-items-center gap-1">
                                  <a href={url.shortUrl} target="_blank" rel="noreferrer" style={{ color: "#0078d4" }}>
                                    {url.qrFormat === "gs1" && url.gtin ? url.gtin : url.urlId}
                                  </a>
                                  <button
                                    className="btn btn-link btn-sm p-0 text-muted"
                                    onClick={() => copyToClipboard(url.shortUrl, url.urlId)}
                                    title="Copy URL"
                                    style={{ lineHeight: 1 }}
                                  >
                                    {copiedId === url.urlId ? (
                                      <span style={{ fontSize: "10px", color: "#198754" }}>copied</span>
                                    ) : (
                                      <CopyIcon />
                                    )}
                                  </button>
                                </span>
                              )}
                            </td>
                            <td className="px-2 align-middle">
                              <a href={`/qr/${url.urlId}.svg`} target="_blank" rel="noreferrer" download>
                                <div className="qr-hover-wrapper">
                                  <img src={`/qr/${url.urlId}.svg`} width="44" height="44" alt="QR" style={{ border: "1px solid #ddd", borderRadius: "3px" }} />
                                  <div className="qr-hover-popup">
                                    <img src={`/qr/${url.urlId}.svg`} width="300" height="300" alt="QR Preview" />
                                  </div>
                                </div>
                              </a>
                            </td>
                            <td className="px-2 align-middle" style={{ maxWidth: "220px" }}>
                              {editingQr === url.urlId ? (
                                <input className="form-control form-control-sm" style={{ fontSize: "11px" }} value={qrForm.destUrl || ""} onChange={(e) => setQrForm({ ...qrForm, destUrl: e.target.value })} />
                              ) : (
                                <span className="text-truncate d-block" style={{ maxWidth: "220px" }}>{url.destUrl || url.origUrl}</span>
                              )}
                            </td>
                            <td className="px-2 align-middle" style={{ maxWidth: "100px" }}>
                              {editingQr === url.urlId ? (
                                <input className="form-control form-control-sm" style={{ fontSize: "11px" }} value={qrForm.notes || ""} onChange={(e) => setQrForm({ ...qrForm, notes: e.target.value })} />
                              ) : (
                                <span className="text-muted text-truncate d-block" style={{ maxWidth: "100px" }}>{url.notes || "—"}</span>
                              )}
                            </td>
                            <td className="px-2 align-middle text-muted">
                              {formatDate(url.date)}
                            </td>
                            <td className="px-2 align-middle text-end">{url.clicks?.toLocaleString()}</td>
                            {canEdit && (
                              <td className="px-2 align-middle text-end text-nowrap">
                                {editingQr === url.urlId ? (
                                  <>
                                    <button className="btn btn-link btn-sm p-0 me-2 text-decoration-none" style={{ fontSize: "11px" }} onClick={handleSaveQr}>Save</button>
                                    <button className="btn btn-link btn-sm p-0 text-decoration-none text-muted" style={{ fontSize: "11px" }} onClick={() => setEditingQr(null)}>Cancel</button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      className="btn btn-link btn-sm p-0 me-3 text-muted"
                                      style={{ lineHeight: 1 }}
                                      onClick={() => { setEditingQr(url.urlId); setQrForm({ side: url.side, destUrl: url.destUrl || url.origUrl, notes: url.notes || "" }); }}
                                      title="Edit"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M4 20h4l10.5-10.5a2.121 2.121 0 0 0-3-3L5 17v3z" />
                                        <path d="M13.5 6.5l3 3" />
                                        <rect x="2" y="2" width="20" height="20" rx="3" ry="3" fill="none" />
                                      </svg>
                                    </button>
                                    <button
                                      className={`btn btn-link btn-sm p-0 me-3 ${url.isDeprecated ? "text-success" : "text-warning"}`}
                                      style={{ lineHeight: 1 }}
                                      onClick={() => toggleDeprecated(url)}
                                      title={url.isDeprecated ? "Restore to active" : "Archive"}
                                    >
                                      {url.isDeprecated ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                          <polyline points="1 4 1 10 7 10" />
                                          <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                                        </svg>
                                      ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="0" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M21 8V21H3V8" fill="currentColor" />
                                          <rect x="1" y="3" width="22" height="5" rx="1" fill="currentColor" />
                                          <line x1="10" y1="12" x2="14" y2="12" stroke="white" strokeWidth="2" />
                                        </svg>
                                      )}
                                    </button>
                                    <button
                                      className="btn btn-link btn-sm p-0 text-muted"
                                      style={{ lineHeight: 1 }}
                                      onClick={() => handleDeleteQr(url.urlId)}
                                      title="Delete"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="3 6 5 6 21 6" />
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        <line x1="10" y1="11" x2="10" y2="17" />
                                        <line x1="14" y1="11" x2="14" y2="17" />
                                      </svg>
                                    </button>
                                  </>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="text-center text-muted py-4">
                      <p className="mb-2">No QR codes yet.</p>
                      {canEdit && (
                        <button className="btn btn-sm btn-success" onClick={() => onAddQr(product)}>
                          + Add QR Code
                        </button>
                      )}
                    </div>
                  )}

                  {/* Download bar */}
                  {selectedQrs.size > 0 && (
                    <div className="d-flex align-items-center justify-content-between p-2 mt-2 rounded" style={{ backgroundColor: "#e8f0fe" }}>
                      <span style={{ fontSize: "13px" }}>
                        {selectedQrs.size} QR code{selectedQrs.size !== 1 ? "s" : ""} selected
                      </span>
                      <button
                        className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                        onClick={downloadSelectedQrs}
                        disabled={downloading}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        {downloading ? "Zipping..." : "Download ZIP"}
                      </button>
                    </div>
                  )}

                  {/* Deleted QR codes */}
                  {canEdit && deletedUrls.length > 0 && (
                    <div className="mt-3">
                      <button
                        className="btn btn-sm btn-outline-secondary mb-2"
                        onClick={() => setShowDeletedQr(!showDeletedQr)}
                        style={{ fontSize: "11px" }}
                      >
                        {showDeletedQr ? "Hide" : "Show"} Deleted QR Codes ({deletedUrls.length})
                      </button>
                      {showDeletedQr && (
                        <table className="table table-sm mb-0" style={{ fontSize: "13px", opacity: 0.6 }}>
                          <thead>
                            <tr style={{ backgroundColor: "#f8f9fa" }}>
                              <th className="px-2">Side</th>
                              <th className="px-2">Short URL</th>
                              <th className="px-2">Destination</th>
                              <th className="px-2">Deleted</th>
                              <th className="px-2">By</th>
                              <th className="px-2"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {deletedUrls.map((url) => (
                              <tr key={url.urlId}>
                                <td className="px-2 text-muted">{url.side || "—"}</td>
                                <td className="px-2 text-muted">{url.urlId}</td>
                                <td className="px-2 text-muted text-truncate" style={{ maxWidth: "200px" }}>
                                  {url.destUrl || url.origUrl || "—"}
                                </td>
                                <td className="px-2 text-muted">{formatDate(url.deletedAt)}</td>
                                <td className="px-2 text-muted">{url.deletedBy || "—"}</td>
                                <td className="px-2 text-end">
                                  <button className="btn btn-link btn-sm p-0 text-decoration-none" style={{ fontSize: "11px" }} onClick={() => restoreQr(url.urlId)}>
                                    Restore
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCardModal;
